import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { AdminPasswordGate } from '../components/AdminPasswordGate';
import { useMetaTags } from '../hooks/useMetaTags';
import { FIND_DEGREES_TEMPLATE_DIRECTORY, getBridgeTemplate } from '../data/bridgeTemplates';

// Internal directory of every /find-degrees/{template} bridge page.
// Grouped by form treatment, because that is the decision someone browsing
// this list is actually making — which ask the traffic should land on.
const FORMATS = ['Call to action', 'Program selector', 'Email capture'] as const;
export function FindDegreesDirectoryPage() {
  useMetaTags({
    title: 'Find Degrees Templates | FindOnlineDegrees',
    description: 'Directory of every Find Degrees bridge page template and what each one does.',
    // An internal index of ad-copy variants shouldn't compete in search.
    robots: 'noindex, nofollow'
  });
  // The shared PageLayout in App.tsx supplies the site nav and footer for
  // this route, so this page renders only its own content.
  return <AdminPasswordGate description="The Find Degrees template directory is for the team. Enter the password to continue." data-id="element-2012" data-mp="1xgtc5pog39ya">
      <div className="bg-white" data-id="element-2013" data-mp="2294cuur0ezrw">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-id="element-2014" data-mp="228uthq0gzqv6">
          <header className="max-w-2xl" data-id="element-2015" data-mp="26951f87torco">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-2016" data-mp="19jdx6h9qbpto">
              Find Degrees templates
            </h1>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed" data-id="element-2017" data-mp="1ejicw52tcgiu">
              Every bridge page live at{' '}
              <code className="text-base text-gray-900" data-id="element-2018" data-mp="1iiivbmhdph5b">
                /find-degrees/&#123;template&#125;
              </code>
              . Each one uses the same underlying flow — only the ad copy, the
              ask, and where the visitor lands afterward change.
            </p>
          </header>

          {FORMATS.map(format => {
          const entries = FIND_DEGREES_TEMPLATE_DIRECTORY.filter(entry => entry.format === format);
          if (!entries.length) return null;
          return <section key={format} className="mt-14" data-id="element-2019" data-mp="18u0c91in514y">
                <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500" data-id="element-2020" data-mp="18tqmnf1icvzs">
                  {format}
                </h2>
                <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2021" data-mp="g5l0cnut2yxu">
                  {entries.map(entry => {
                const template = getBridgeTemplate(entry.id);
                return <li key={entry.id} className="flex" data-id="element-2022" data-mp="pvo15a865h6q">
                        <Link to={`/find-degrees/${entry.id}`} className="group flex w-full flex-col rounded-2xl border border-gray-200 p-6 transition-colors hover:border-[#e8594f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f] focus-visible:ring-offset-2" data-id="element-2023" data-mp="px7il45k9ya3">
                          <span className="text-xs font-semibold text-gray-400" data-id="element-2024" data-mp="242kdtnufllwj">
                            /find-degrees/{entry.id}
                          </span>
                          <h3 className="mt-2 text-lg font-bold text-gray-900" data-id="element-2025" data-mp="1v2xk7o7y1ke1">
                            {entry.name}
                          </h3>
                          <p className="mt-2 text-sm text-gray-600 leading-relaxed" data-id="element-2026" data-mp="1z4ivx68qrsmn">
                            {entry.summary}
                          </p>
                          <dl className="mt-4 space-y-1 text-xs text-gray-500" data-id="element-2027" data-mp="2rt6mnhc9fa7l">
                            <div className="flex gap-1.5" data-id="element-2028" data-mp="2dtfc5qkycbyc">
                              <dt className="font-semibold text-gray-700" data-id="element-2029" data-mp="2dsvvseay9vmf">
                                Button:
                              </dt>
                              <dd className="truncate" data-id="element-2030" data-mp="12dgx9633evp7">{template.ctaLabel}</dd>
                            </div>
                            <div className="flex gap-1.5" data-id="element-2031" data-mp="17dd032020h36">
                              <dt className="font-semibold text-gray-700" data-id="element-2032" data-mp="17aqngnyb3be3">
                                Goes to:
                              </dt>
                              <dd className="truncate" data-id="element-2033" data-mp="1hv866fau8vzk">{entry.destination}</dd>
                            </div>
                          </dl>
                          <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#e8594f]" data-id="element-2034" data-mp="1cukfxvufoj6e">
                            View page
                            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-2035" data-mp="25l2j0wv5qkjt" />
                          </span>
                        </Link>
                      </li>;
              })}
                </ul>
              </section>;
        })}
        </div>
      </div>
    </AdminPasswordGate>;
}